import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState } from 'react';
const hours = value => value == null ? 'Not recorded' : `${Number(value.toFixed(2))} h`;
const time = value => String(value || '').slice(0, 5);
export default function WorkforceScheduleGrid({
  dbName,
  token,
  printMode = false
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    setSelected(null);
    if (!dbName) return;
    fetch(`${API_ORIGIN}/api/headoffice/compliance/workforce-schedule?dbName=${encodeURIComponent(dbName)}`, {
      headers: token ? {
        Authorization: `Bearer ${token}`
      } : {}
    }).then(async response => {
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || `HTTP ${response.status}`);
      if (!cancelled) setData(json);
    }).catch(error => {
      if (!cancelled) setError(error.message);
    });
    return () => {
      cancelled = true;
    };
  }, [dbName, token]);
  if (!dbName) return <div>Scheduled staffing is unavailable: branch not resolved.</div>;
  if (error) return <div role="alert">Could not load scheduled staffing: {error}</div>;
  if (!data) return <div role="status">Loading scheduled staffing…</div>;
  const cellStyle = {
    padding: '8px',
    borderBottom: '1px solid #dbe3eb',
    textAlign: 'left',
    verticalAlign: 'top'
  };
  const renderDetail = (role, dayIndex) => <section style={{
    marginTop: 12,
    padding: 12,
    border: '1px solid #b6d4ef',
    borderRadius: 8,
    background: '#fff',
    color: '#243447'
  }}>
    <h4 style={{
      margin: '0 0 8px'
    }}>{role.role} — {data.days[dayIndex].name}</h4>
    <div style={{
      overflowX: 'auto'
    }}><table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12
      }}>
      <thead><tr>{['Staff member', 'Week 1 shifts', 'Break', 'Week 1 net hours', 'Week 2 shifts', 'Break', 'Week 2 net hours', 'Daily average'].map(title => <th key={title} scope="col" style={cellStyle}>{title}</th>)}</tr></thead>
      <tbody>{role.days[dayIndex].staff.map(person => <tr key={person.staffId}>
        <th scope="row" style={cellStyle}>{person.name}</th>
        {person.weeks.map((week, index) => <React.Fragment key={index}>
          <td style={cellStyle}>{week.issue || (week.shifts.length ? week.shifts.map((shift, i) => <div key={i}>{time(shift.start)}–{time(shift.end)} ({hours(shift.hours)})</div>) : 'Day off')}</td>
          <td style={cellStyle}>{week.breakMinutes == null ? 'Not recorded' : `${week.breakMinutes} min`}</td>
          <td style={cellStyle}>{hours(week.hours)}</td>
        </React.Fragment>)}
        <td style={cellStyle}>{hours(person.hours)}</td>
      </tr>)}</tbody>
    </table></div>
  </section>;
  return <section aria-label="Scheduled staffing by role and weekday" style={{
    background: '#f5f9fd',
    color: '#243447',
    padding: 12,
    borderRadius: 8
  }}>
    <h3 style={{
      margin: '0 0 8px',
      fontSize: 16
    }}>Scheduled staffing — hours by role and weekday</h3>
    <p style={{
      fontSize: 12
    }}>Net hours are the average of Week 1 and Week 2 in each staff member’s 14-day schedule. Split shifts are added and the daily break is deducted once. A day off counts as zero. Click a role’s daily hours to see names and both weeks’ shifts.</p>
    {data.missingSchedules.length > 0 && <p role="status" style={{
      color: '#92400e'
    }}>14-day schedules missing: {data.missingSchedules.join(', ')}. Affected totals show “Not recorded”.</p>}
    <div style={{
      overflowX: 'auto'
    }}><table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
        minWidth: 760
      }}>
      <thead><tr><th scope="col" style={cellStyle}>Role / opening hours</th>{data.days.map(day => <th key={day.index} scope="col" style={cellStyle}>{day.name}</th>)}</tr></thead>
      <tbody>
        <tr style={{
            background: '#e2edf8'
          }}><th scope="row" style={cellStyle}>Pharmacy opening hours</th>{data.days.map(day => <td key={day.index} style={cellStyle}>{day.openingHours == null ? 'Not recorded' : day.openingHours === 0 ? 'Closed' : <>{day.slots.map((slot, i) => <div key={i}>{time(slot.start)}–{time(slot.end)}</div>)}<b>{hours(day.openingHours)}</b></>}</td>)}</tr>
        {data.roles.map(role => <tr key={role.roleId}><th scope="row" style={cellStyle}>{role.role}</th>{role.days.map((day, index) => <td key={index} style={cellStyle}><button type="button" aria-label={`${role.role}, ${data.days[index].name}: ${hours(day.hours)}; show staff shifts`} aria-expanded={selected?.roleId === role.roleId && selected?.day === index} onClick={() => setSelected(current => current?.roleId === role.roleId && current?.day === index ? null : {
                roleId: role.roleId,
                day: index
              })} style={{
                background: '#fff',
                color: '#155a94',
                border: '1px solid #b6d4ef',
                borderRadius: 5,
                padding: '6px 8px',
                cursor: 'pointer'
              }}>{hours(day.hours)}</button></td>)}</tr>)}
        <tr style={{
            fontWeight: 700
          }}><th scope="row" style={cellStyle}>Total scheduled staff hours</th>{data.totals.map((value, index) => <td key={index} style={cellStyle}>{hours(value)}</td>)}</tr>
      </tbody>
    </table></div>
    {!data.roles.length && <p>No active staff recorded.</p>}
    <p style={{
      fontSize: 11
    }}>Source: pharmacy opening hours and active staff schedules in Pharmace. These are scheduled staff hours after breaks, not attendance hours. Daily totals help review staffing provision; they do not establish continuous cover or a required staffing level.</p>
    {printMode ? data.roles.flatMap(role => data.days.map(day => <React.Fragment key={`${role.roleId}-${day.index}`}>{renderDetail(role, day.index)}</React.Fragment>)) : selected && data.roles.filter(role => role.roleId === selected.roleId).map(role => <React.Fragment key={role.roleId}>{renderDetail(role, selected.day)}</React.Fragment>)}
  </section>;
}
